import React from 'react';
import { Provider } from 'react-redux';
import './i18n';
import { createRoot } from 'react-dom/client';
import { createScope, cssUtilities, partnerCenterTheme } from '@harmony/enablers/react';
import { ICustomProperties, IMetricTelemetry } from '@microsoft/applicationinsights-web';
import AppEnvironment from './environment/app-environment';
import { store } from './ducks/store';
import { MEMBootstrap } from './bootstrap';
import serviceLogger from './services/logging/service-logger';
import servicePerformance from './services/performance-observer/service-performance-observer';
import { reportAccessibility } from './utils/report-accessibility';

const container = document.getElementById('MemSellerExperienceMainContent');
const root = createRoot(container!);

// Create a global scope to get cssutilities and partnerCenterTheme globally
createScope({
  styles: [partnerCenterTheme, cssUtilities],
});

// Start mocking conditionally
switch (AppEnvironment.env) {
  case 'local':
    void import('./mocks/browser-partial')
      .then(({ worker }) => worker.start())
      .catch((error) => console.error('Failed to start mock worker for local environment:', error));
    break;
  case 'mocked':
    void import('./mocks/browser')
      .then(({ worker }) => worker.start())
      .catch((error) => console.error('Failed to start mock worker for mocked environment:', error));
    break;
  case 'mocked-ubi':
    void import('./mocks/browser-ubi')
      .then(({ worker }) => worker.start())
      .catch((error) => console.error('Failed to start mock worker for mocked-ubi environment:', error));
    break;
  case 'mocked-v2':
    void import('./mocks-v2/browser-integration')
      .then(({ setupMocksV2Worker }) => {
        const worker = setupMocksV2Worker();
        return worker.start();
      })
      .catch((error) => console.error('Failed to start mock worker for mocked-v2 environment:', error));
    break;
  case 'mocked-spl':
    void import('./mocks/browser-spl')
      .then(({ worker }) => worker.start())
      .catch((error) => console.error('Failed to start mock worker for mocked-spl environment:', error));
    break;
  default:
    break;
}

// Initialize AI logging
serviceLogger.init();

// Initialize performance observer
servicePerformance.init('mem', AppEnvironment.env, (logPayload: IMetricTelemetry, customProperties?: ICustomProperties) =>
  serviceLogger.logMetric(logPayload, customProperties)
);

const AppRoot = () => (
  <React.StrictMode>
    <Provider store={store}>
      <MEMBootstrap />
    </Provider>
  </React.StrictMode>
);

root.render(<AppRoot />);

// This will only run on non-prod or non-preprod environments
void reportAccessibility(React);
